import React, { useEffect, useState, useCallback, useRef } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models, Report } from "powerbi-client";
import { Spin, Alert, Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { getEmbedConfigApi } from "../../services/PowerBI/PowerBIApis";

interface EmbedConfig {
  accessToken: string;
  embedUrl: string;
  reportId: string;
}

interface PaginatedReportEmbedProps {
  visible: boolean;
  reportId?: string;
  datasetId?: string;
  groupId?: string | number;
}

// ─── Component ────────────────────────────────────────────────────────────────
const PaginatedReportEmbed: React.FC<PaginatedReportEmbedProps> = ({ visible, reportId, datasetId, groupId }) => {
  const [embedConfig, setEmbedConfig] = useState<EmbedConfig | null>(null);
  const [loadingMsg, setLoadingMsg]   = useState<string | null>(null);
  const [error, setError]             = useState<string | null>(null);
  const [noMembers, setNoMembers]     = useState<boolean>(false);
  const reportRef = useRef<Report | null>(null);

  const viewReport = useCallback(async () => {
    setLoadingMsg("Loading Power BI report…");
    setError(null);
    setNoMembers(false);
    setEmbedConfig(null);

    try {
      const data = await getEmbedConfigApi(reportId, datasetId, groupId);

      // Treat explicit "no members" responses as a friendly warning.
      const isNoMembers =
        data === false ||
        data === null ||
        data?.success === false ||
        data?.data === false ||
        data?.data === null ||
        data?.noMembers === true ||
        (typeof data?.message === "string" && /no\s*members?/i.test(data.message));

      if (isNoMembers) {
        setNoMembers(true);
        return;
      }

      if (!data || !data.accessToken || !data.embedUrl || !data.reportId) {
        // No explicit error status/message → most likely no members for this group.
        const status = data?.status ?? data?.statusCode;
        const explicitMsg = data?.message || data?.error;
        if (!status && !explicitMsg) {
          setNoMembers(true);
          console.warn("[PaginatedReportEmbed] empty embed config response, treating as no members:", data);
          return;
        }
        const msg =
          explicitMsg ||
          (status
            ? `The reports service returned an error (${status}).`
            : "The reports service is currently unavailable.");
        setError(msg);
        console.error("[PaginatedReportEmbed] invalid embed config response:", data);
        return;
      }

      setEmbedConfig({ accessToken: data.accessToken, embedUrl: data.embedUrl, reportId: data.reportId });
    } catch (err: any) {
      setError(err?.message ?? "An unexpected error occurred.");
      console.error("[PaginatedReportEmbed]", err);
    } finally {
      setLoadingMsg(null);
    }
  }, [reportId, datasetId, groupId]);

  // Auto-load when modal opens; cleanup when it closes
  useEffect(() => {
    if (visible) {
      viewReport();
    } else {
      if (reportRef.current) {
        try { reportRef.current.off("loaded"); reportRef.current.off("error"); } catch (_) {}
        reportRef.current = null;
      }
      setEmbedConfig(null);
      setLoadingMsg(null);
      setError(null);
      setNoMembers(false);
    }
  }, [visible, viewReport]);

  // ── Loading / error ──────────────────────────────────────────────────────
  if (!embedConfig) {
    if (noMembers) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 520, gap: 20 }}>
          <Alert
            type="warning"
            showIcon
            message="No Members Found"
            description="There are no members available for this report."
            style={{ width: "100%", maxWidth: 520 }}
          />
          <Button
            size="large"
            icon={<ReloadOutlined />}
            onClick={viewReport}
            style={{ borderColor: "#722ed1", color: "#722ed1" }}
          >
            Retry
          </Button>
        </div>
      );
    }
    if (error) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 520, gap: 20 }}>
          <Alert
            type="error"
            showIcon
            message="Error"
            description={error}
            style={{ width: "100%", maxWidth: 520 }}
          />
          <Button
            size="large"
            icon={<ReloadOutlined />}
            onClick={viewReport}
            style={{ borderColor: "#722ed1", color: "#722ed1" }}
          >
            Retry
          </Button>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 520, gap: 16 }}>
        <Spin size="large" />
        <span style={{ color: "#722ed1", fontSize: 14 }}>{loadingMsg ?? "Loading…"}</span>
      </div>
    );
  }

  // ── Embedded report ──────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10, display: "flex", gap: 8, alignItems: "center" }}>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={viewReport}
          style={{ borderColor: "#722ed1", color: "#722ed1" }}
        >
          Refresh
        </Button>
      </div>

      <PowerBIEmbed
        embedConfig={{
          type: "report",
          id: embedConfig.reportId,
          embedUrl: embedConfig.embedUrl,
          accessToken: embedConfig.accessToken,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: {
              filters: { visible: false, expanded: false },
              pageNavigation: { visible: false },
            },
            background: models.BackgroundType.Transparent,
          },
        }}
        eventHandlers={
          new Map<string, (event: any) => void>([
            ["loaded", () => { console.log("[PowerBI] ✓ Report loaded successfully"); }],
            ["error",  (event: any) => {
              const msg = event?.detail?.message ?? "Unknown error";
              console.error("[PowerBI] Report runtime error:", event?.detail);
              setError("Report error: " + msg);
              setEmbedConfig(null);
            }],
          ])
        }
        getEmbeddedComponent={(embedded) => { reportRef.current = embedded as Report; }}
        cssClassName="powerbi-embed-container"
      />
    </div>
  );
};

export default PaginatedReportEmbed;

